import { type FC, type ReactNode } from "react";
import { Plus } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { getEstateCategoriesForDirection, CATEGORY_DOT_COLORS, type CategoryConfig } from "@/pages/postloss/estate-inventory/constants/transactionCategories";

interface CategoryPickerProps {
  isInflow?: boolean;
  onSelect: (category: string) => void;
  children?: ReactNode;
}

const CategoryMenuItem: FC<{ config: CategoryConfig; onSelect: () => void }> = ({ config, onSelect }) => (
  <DropdownMenuItem
    onSelect={onSelect}
    className="flex items-center gap-3 px-3 py-2 h-auto"
  >
    <div className={`h-2 w-2 shrink-0 rounded-full ${CATEGORY_DOT_COLORS[config.color] ?? "bg-stone-400"}`} />
    <div>
      <p className="text-sm font-medium">{config.label}</p>
      {config.label !== "Other" ? (
        <p className="text-xs text-secondary">{config.tooltip}</p>
      ) : null}
    </div>
  </DropdownMenuItem>
);

export const CategoryPicker: FC<CategoryPickerProps> = ({ isInflow, onSelect, children }) => {
  const direction = isInflow ? "in" : "out";
  const categories = getEstateCategoriesForDirection(direction).filter((c) => c.label !== "Other");
  const other = getEstateCategoriesForDirection(direction).find((c) => c.label === "Other");
  const sectionLabel = isInflow ? "Money In" : "Money Out";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {children ?? (
          <button
            type="button"
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-dashed border-primary text-xs text-secondary hover:border-stone-400 hover:text-primary transition-colors"
          >
            <Plus className="h-3 w-3" />
            Add category
          </button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72 max-h-[400px] overflow-y-auto">
        <DropdownMenuLabel className="px-3 py-2 text-sm font-medium text-secondary">
          {sectionLabel}
        </DropdownMenuLabel>
        {categories.map((config) => (
          <CategoryMenuItem key={config.label} config={config} onSelect={() => onSelect(config.label)} />
        ))}
        {other ? (
          <>
            <DropdownMenuSeparator />
            <CategoryMenuItem config={other} onSelect={() => onSelect(other.label)} />
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
